import camelCase from 'lodash-es/camelCase'
import compact from 'lodash-es/compact'
import find from 'lodash-es/find'
import flatMapDeep from 'lodash-es/flatMapDeep'
import flattenDeep from 'lodash-es/flattenDeep'
import get from 'lodash-es/get'
import has from 'lodash-es/has'
import isEmpty from 'lodash-es/isEmpty'
import isEqual from 'lodash-es/isEqual'
import _isFinite from 'lodash-es/isFinite'
import map from 'lodash-es/map'
import sample from 'lodash-es/sample'
import upperFirst from 'lodash-es/upperFirst'
import ApolloClient from '~/client'
import store, { RootState } from '~/store'
import CLASS_NAMES, { ObjectClassName } from 'config/objectClassNames'
import Util from 'components/DrawingCanvas/lib/util'
import ObstructionUtil from 'components/DrawingCanvas/lib/obstructionUtil'
import theme from 'config/theme'

import * as THREE from 'three'
import { graphql } from '~/gql'
import { ObjectsState } from './reducer'
import { StateObject } from './types'
import { createSelector } from '@reduxjs/toolkit'
import {
  denormalizeWalls,
  memoizedDoorMesh,
  memoizedObstructionMesh,
  memoizedProductMesh,
  memoizedRoofMeshes,
  memoizedUtilityBoxMesh,
} from '~/components/DrawingCanvas/lib/collisionMeshes'
import { ApolloClient as ApolloClientType, NormalizedCacheObject } from '@apollo/client'

export const COPYABLE_CLASS_NAMES = [
  CLASS_NAMES.OBSTRUCTION,
  CLASS_NAMES.PRODUCT,
  CLASS_NAMES.DOOR,
  CLASS_NAMES.UTILITY_BOX,
  CLASS_NAMES.COMFORT_ZONE,
  CLASS_NAMES.DIMENSION,
]

export function utilityBoxes(state: RootState) {
  return get(state, 'objects.present.utilityBoxes')
}

export function objectClassNameFromStateKey(stateKey: string): ObjectClassName {
  switch (stateKey) {
    case 'objects': {
      return CLASS_NAMES.WALL
    }
    case 'segments': {
      return CLASS_NAMES.WALL_SEGMENT
    }
    case 'utilityBoxes': {
      return CLASS_NAMES.UTILITY_BOX
    }
    case 'airflow': {
      return CLASS_NAMES.AIRFLOW
    }
    case 'heatMap': {
      return CLASS_NAMES.HEAT_MAP
    }
    default: {
      return upperFirst(camelCase(stateKey)).replace(/s$/, '') as ObjectClassName
    }
  }
}

export function objectStateKeyFromClassName(className: string): keyof ObjectsState {
  switch (className) {
    case CLASS_NAMES.WALL: {
      return 'objects'
    }
    case CLASS_NAMES.WALL_SEGMENT: {
      return 'segments'
    }
    case CLASS_NAMES.UTILITY_BOX: {
      return 'utilityBoxes'
    }
    case CLASS_NAMES.AIRFLOW: {
      return 'airflow'
    }
    case CLASS_NAMES.HEAT_MAP: {
      return 'heatMap'
    }
    default: {
      return `${camelCase(className)}s` as keyof ObjectsState
    }
  }
}

export function getObjectLibrary(state: RootState): (StateObject & { className: ObjectClassName })[] {
  const present = get(state, 'objects.present')

  const library = flatMapDeep(present, (obj, stateKey) =>
    map(
      obj,
      objProps =>
        has(objProps, 'id') && {
          ...objProps,
          className: objectClassNameFromStateKey(stateKey), // TODO: do we need this?
        }
    )
  )
  return compact(library)
}

export function getObjectImageIds() {
  const objects = get(store.getState(), 'objects.present')

  // Get all of the object keys
  const ids = Object.keys(objects)
    // Filter out any empty objects
    .filter(fo => !isEmpty(objects[fo as keyof ObjectsState]))
    // Go through each object
    .map(object => {
      // Go through each nested object
      return Object.keys(objects[object as keyof ObjectsState]).map(no => {
        // If there is a nested object *AND* it's an object
        if (
          objects[object as keyof ObjectsState][no] &&
          typeof objects[object as keyof ObjectsState][no] === 'object'
        ) {
          // If the object has the `metadata.images` key
          if (get(objects[object as keyof ObjectsState][no], 'metadata.images')) {
            // Return it
            return get(objects[object as keyof ObjectsState][no], 'metadata.images').map(
              (image: any) => image.cloudinaryId
            )
          }
        }

        // Otherwise, return `null`
        return null
      })
    })

  return compact(flattenDeep(ids))
}

export function findObject(
  predicate: (object: StateObject & { className: ObjectClassName }) => boolean,
  { state = store.getState() } = {}
) {
  const objectLibrary = getObjectLibrary(state)
  const returnObject = find(objectLibrary, predicate)
  return returnObject
}

// Searches apollo cache
function findProductVariation(variationId: string) {
  return ApolloClient.readFragment({
    id: `ProductVariation:${variationId}`,
    fragment: graphql(`
      fragment DuplicateVariationFragment on ProductVariation {
        id
        size
        minProductClearance
      }
    `),
  })
}

export function identifyNextDuplicateOffset<T extends keyof ObjectsState>(
  object: ObjectsState[T],
  stateKey: T,
  state = store.getState(),
  attempt = 1,
  prevOffset?: { x: number; y: number }
): { x: number; y: number } {
  const objects = get(state, `objects.present.${stateKey}`)
  const isEvenAttempt = attempt % 2 === 0
  let multiplier = isEvenAttempt ? attempt / 2 : attempt - (attempt - 1) / 2

  const defaultOffset = 50
  const offset = { x: 0, y: 0 }
  let xDiff
  let yDiff
  switch (stateKey) {
    case 'products': {
      const variation = findProductVariation(object.variationId)
      offset.x = isEvenAttempt ? 0 : (variation!.size + (variation!.minProductClearance || 0) / 2) * multiplier
      offset.y = isEvenAttempt ? (variation!.size + (variation!.minProductClearance || 0) / 2) * multiplier : 0
      break
    }
    case 'obstructions':
    case 'comfortZones': {
      const positions = ObstructionUtil.getRotatedPositions(
        object.positions,
        object.position,
        object.rotation && object.rotation.z
      )
      ;({ xDiff, yDiff } = ObstructionUtil.getPositionDiffs(positions))
      offset.x = isEvenAttempt ? 0 : xDiff * multiplier
      offset.y = isEvenAttempt ? yDiff * multiplier : 0
      break
    }
    case 'doors': {
      const rotation = object.rotation
      const doorBuffer = 10
      multiplier = multiplier * (isEvenAttempt ? -1 : 1)
      offset.x = multiplier * Math.cos(rotation._z) * (object.width + doorBuffer)
      offset.y = multiplier * Math.sin(rotation._z) * (object.width + doorBuffer)
      break
    }
    case 'utilityBoxes': {
      const svRotation = object.rotation
      const utilityBoxBuffer = 10
      multiplier = multiplier * (isEvenAttempt ? -1 : 1)
      offset.x = multiplier * Math.cos(svRotation._z) * (object.width + utilityBoxBuffer)
      offset.y = multiplier * Math.sin(svRotation._z) * (object.width + utilityBoxBuffer)
      break
    }
    case 'dimensions':
      xDiff = Math.abs(object.startPos.x - object.endPos.x)
      yDiff = Math.abs(object.startPos.y - object.endPos.y)

      if (xDiff > yDiff) {
        offset.y = defaultOffset * attempt
      } else {
        offset.x = defaultOffset * attempt
      }
      break
    default:
      offset.x = isEvenAttempt ? 0 : defaultOffset * multiplier
      offset.y = isEvenAttempt ? defaultOffset * multiplier : 0
      break
  }

  let nextOffsetAlreadyExists = false
  if (stateKey === 'dimensions') {
    const startPos = Object.assign({}, object.startPos)
    const endPos = Object.assign({}, object.endPos)
    startPos.x = startPos.x + offset.x
    endPos.x = endPos.x + offset.x
    startPos.y = startPos.y + offset.y
    endPos.y = endPos.y + offset.y

    nextOffsetAlreadyExists =
      find(state.objects.present.dimensions, o => isEqual(o.startPos, startPos) && isEqual(o.endPos, endPos)) !==
      undefined
  } else {
    const newPosition = { ...object.position }
    newPosition.x = object.position.x + offset.x
    newPosition.y = object.position.y + offset.y

    nextOffsetAlreadyExists = find(objects, (o: any) => isEqual(o.position, newPosition)) !== undefined
  }
  // Error on infinite loops and bad values
  if (isEqual(prevOffset, offset) || !_isFinite(offset.x) || !_isFinite(offset.y))
    throw new Error('Logic Error! Offset is resolving to the same result')
  if (nextOffsetAlreadyExists) return identifyNextDuplicateOffset(object, stateKey, state, attempt + 1, offset)

  return offset
}

export const tallestWallHeight = createSelector(
  (store: RootState) => store.objects.present.segments,
  segments => {
    return Object.values(segments).reduce((prev, curr) => Math.max(prev, curr.height), -Infinity)
  }
)

export const facilityBounds = createSelector(
  [(store: RootState) => store.objects.present.segments, (store: RootState) => store.objects.present.objects],
  (segments, walls) => {
    const denormalizedSegments = denormalizeWalls(segments, walls)
      .map(({ segments }) => segments)
      .flat()
    const allPoints = denormalizedSegments.flatMap(segment => [
      segment.startPoint,
      segment.endPoint,
      segment.insetEdge.start,
      segment.insetEdge.end,
      segment.outsetEdge.start,
      segment.outsetEdge.end,
    ])

    const minX = allPoints.reduce((prev, curr) => Math.min(prev, curr.x), +Infinity)
    const minY = allPoints.reduce((prev, curr) => Math.min(prev, curr.y), +Infinity)
    const maxX = allPoints.reduce((prev, curr) => Math.max(prev, curr.x), -Infinity)
    const maxY = allPoints.reduce((prev, curr) => Math.max(prev, curr.y), -Infinity)

    return {
      width: maxX - minX,
      height: maxY - minY,
    }
  }
)

export const facilityIsRectangle = createSelector(
  (store: RootState) => store.objects.present.objects,
  (store: RootState) => store.objects.present.segments,
  (walls, segments) => {
    const EPSILON = 1

    const wallsWithRoofs = Object.values(walls).filter(wall => wall.roofId !== null)
    if (wallsWithRoofs.length !== 1) {
      return false
    }

    const segs = wallsWithRoofs[0].segments.map(segment => segments[segment])
    if (segs.length !== 4) {
      return false
    }

    const points = segs.map(segment => segment.startPoint)
    points.push(segs[segs.length - 1].endPoint)

    // opposing sides must be equal
    const d1 = Util.distanceToLineSegment(points[0].x, points[0].y, points[1].x, points[1].y, points[2].x, points[2].y)
    const d2 = Util.distanceToLineSegment(points[1].x, points[1].y, points[2].x, points[2].y, points[3].x, points[3].y)
    const d3 = Util.distanceToLineSegment(points[2].x, points[2].y, points[3].x, points[3].y, points[4].x, points[4].y)
    const d4 = Util.distanceToLineSegment(points[3].x, points[3].y, points[0].x, points[0].y, points[1].x, points[1].y)

    if (d1 - d3 > EPSILON || d2 - d4 > EPSILON) return false

    return true
  }
)

async function objectMesh(
  client: ApolloClientType<NormalizedCacheObject>,
  selected: { id: string; className: string },
  state: ObjectsState
) {
  if (selected.className === ObjectClassName.PRODUCT) {
    return memoizedProductMesh(client, state.products[selected.id])
  } else if (selected.className === ObjectClassName.DOOR) {
    return memoizedDoorMesh(state.doors[selected.id], state.segments[state.doors[selected.id].wallSegmentId])
  } else if (selected.className === ObjectClassName.OBSTRUCTION) {
    const roofMeshes = memoizedRoofMeshes(state.roofs, state.elevationLines, state.elevationPoints)

    return memoizedObstructionMesh(state.obstructions[selected.id], roofMeshes)
  } else if (selected.className === ObjectClassName.UTILITY_BOX) {
    return memoizedUtilityBoxMesh(state.utilityBoxes[selected.id])
  } else if (selected.className === ObjectClassName.COMFORT_ZONE) {
    // Irrelevant now that the canvas has been refactored
  }
  throw new Error(`invalid object className ${selected.className}`)
}

export async function getObjectsForDuplication(
  client: ApolloClientType<NormalizedCacheObject>,
  selected: { id: string; className: string }[],
  state: ObjectsState
) {
  let initialized = false
  const box = new THREE.Box3()
  for (const item of selected) {
    if (!initialized) {
      initialized = true
      box.setFromObject(await objectMesh(client, item, state))
    } else {
      box.expandByObject(await objectMesh(client, item, state))
    }
  }
  const offset = box.getSize(new THREE.Vector3()).x + 1

  // Create duplicate objects and update their position
  const newObjects: any[] = []
  for (const obj of selected) {
    const stateKey = objectStateKeyFromClassName(obj.className)
    const stateObject = state[stateKey][obj.id]

    if (stateObject) {
      const newObject = Object.assign({}, stateObject)
      newObject.id = Util.guid()
      newObject.className = obj.className
      if (stateObject.position) {
        const newPos = {
          x: stateObject.position.x + offset,
          y: stateObject.position.y,
          z: stateObject.position.z,
        }
        newObject.position = { ...newPos }
      }
      if (stateObject.positions) {
        newObject.positions = stateObject.positions.map((pos: THREE.Vector3Like) => ({
          x: pos.x + offset,
          y: pos.y,
          z: pos.z,
        }))
      }

      newObjects.push(newObject)
    }
  }

  return newObjects
}

export function getObjectForDuplication(
  object: { className: string; id: string | number },
  offset: { x: any; y: any } | undefined
) {
  const state = store.getState().objects.present
  const stateKey = objectStateKeyFromClassName(object.className)
  const stateObject = state[stateKey][object.id]

  if (!stateObject) return

  const newObject = Object.assign({}, stateObject)

  switch (object.className) {
    case CLASS_NAMES.COMFORT_ZONE:
      newObject.color = sample(theme.colors.swatches)
      break
    default:
      break
  }

  if (!offset) {
    offset = identifyNextDuplicateOffset(stateObject, stateKey)
  }

  // Offset the new object so it's not on top of the original
  if (stateObject.startPos) {
    const newPos = {
      x: stateObject.startPos.x + offset.x,
      y: stateObject.startPos.y + offset.y,
      z: stateObject.startPos.z,
    }
    newObject.startPos = { ...newPos }
  }
  if (stateObject.endPos) {
    const newPos = {
      x: stateObject.endPos.x + offset.x,
      y: stateObject.endPos.y + offset.y,
      z: stateObject.endPos.z,
    }
    newObject.endPos = { ...newPos }
  }
  if (stateObject.position) {
    const newPos = {
      x: stateObject.position.x + offset.x,
      y: stateObject.position.y + offset.y,
      z: stateObject.position.z,
    }
    newObject.position = { ...newPos }
  }
  if (stateObject.positions) {
    newObject.positions = stateObject.positions.map((pos: THREE.Vector3Like) => ({
      x: pos.x + offset.x,
      y: pos.y + offset.y,
      z: pos.z,
    }))
  }

  newObject.id = Util.guid()

  return newObject
}

export function getCeilings() {
  return store.getState().objects.present.ceilings
}

export function getRoofs() {
  return store.getState().objects.present.roofs
}

export function findObjectById(
  state: ObjectsState,
  id: string
): [StateObject, keyof ObjectsState] | [undefined, undefined] {
  for (const key_ in state) {
    const key = key_ as keyof ObjectsState
    if (state[key] && typeof state[key] === 'object' && id in state[key]) {
      return [(state as any)[key][id], key]
    }
  }
  return [undefined, undefined]
}

export function findSpecificObjectById<T extends keyof ObjectsState>(
  state: ObjectsState,
  key: T,
  id: string
): ObjectsState[T][string] | undefined {
  const [obj, k] = findObjectById(state, id)
  if (k === key) {
    return obj as any
  }
  return undefined
}
